<template>
  <base-layout>
    <div class="col-xs-12 main" slot="main">
      <div class="col-xs-12 col-md-9 col-lg-10 slots">
        <div class="dashboard-header">
          <h2>Agents: Availability</h2>
        </div>

        <div class="summary" v-if="slots && !isEmpty">
          <span v-for="t in summary.types" :key="t.type" class="summary-card"
                :title="t.total + ' agent-slots available over ' + summary.days + ' days'">
            <span class="label" :class="labelClass(t.type)"><i class="fa" :class="iconClass(t.type)"></i></span>
            <strong>{{ t.total }}</strong> {{ t.label }}
          </span>
          <span class="summary-card metric"><strong>{{ summary.days }}</strong> days</span>
          <span class="summary-card metric" title="Most agents available for one appointment type in a single slot">
            <strong>{{ summary.peak }}</strong> peak<span class="muted" v-if="summary.peakTime"> @ {{ summary.peakTime }}</span>
          </span>
        </div>

        <transition name="fade">
          <div v-if="slots" class="panel stats">
            <div class="panel-body">
              <div v-if="isEmpty" class="text-center no-data">
                No availability found for the selected agents.
              </div>
              <div v-else class="table-responsive">
                <table class="table slots-table">
                  <thead>
                    <tr>
                      <th v-for="(column, index) in slots.header" :key="index">{{ column }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="hour in hours" :key="hour">
                      <th>{{ hour }}</th>
                      <td v-for="(day, index) in days" :key="index">
                        <span v-for="(count, type) in cellTypes(hour, day)" :key="type"
                              class="label" :class="labelClass(type)" :title="appointmentTypeLabels[type]">
                          <i class="fa" :class="iconClass(type)"></i> {{ count }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <transition name="fade">
        <div v-show="filters" class="col-xs-12 col-md-3 col-lg-2">
          <filters :filters="filters" :renderSearch="false" @applyFilters="applyFilters"></filters>
        </div>
      </transition>
    </div>
    <div slot="modals">
      <loading-modal v-if="!loaded"></loading-modal>
    </div>
  </base-layout>
</template>

<script>
  import Vue from 'vue';
  import Filters from 'components/elements/Filters';
  import {BaseLayout} from 'components/layouts/';
  import {LoadingModal} from 'components/modals';

  // Load the full build.
  var _ = require('lodash');

  // Keep these aligned with apps/agents/widgets.py (AgentsSlots) on the backend.
  const APPOINTMENT_TYPE_LABELS = {
    'phone-call': 'Phone Call',
    'online': 'Online Meeting',
    'in-person-at-home': "Beneficiary's Location",
    'in-person-at-office': "Agent's Office"
  };
  const APPOINTMENT_TYPE_COLORS = {
    'phone-call': 'label-success',
    'online': 'label-info',
    'in-person-at-home': 'label-warning',
    'in-person-at-office': 'label-primary'
  };
  const APPOINTMENT_TYPE_ICONS = {
    'phone-call': 'fa-phone',
    'online': 'fa-video-camera',
    'in-person-at-home': 'fa-home',
    'in-person-at-office': 'fa-building-o'
  };

  export default {
    components: {
      Filters,
      BaseLayout,
      LoadingModal
    },
    data () {
      return {
        loaded: false,
        slots: null,
        filters: null,
        params: {},
        appointmentTypeLabels: APPOINTMENT_TYPE_LABELS
      }
    },
    async created () {
      await this.getData(true);
    },
    computed: {
      days () {
        return this.slots ? this.slots.header.slice(1) : [];
      },
      // Render a continuous hourly grid bounded by the earliest and latest
      // slot times; empty hours within that range still show as blank rows.
      hours () {
        if (!this.slots) {
          return [];
        }
        let keys = Object.keys(this.slots.rows);
        if (keys.length === 0) {
          return [];
        }
        let hourNums = keys.map(function (key) { return parseInt(key.slice(0, 2), 10); });
        let min = Math.min.apply(null, hourNums);
        let max = Math.max.apply(null, hourNums);
        let result = [];
        for (let h = min; h <= max; h++) {
          result.push((h < 10 ? '0' + h : '' + h) + ':00');
        }
        return result;
      },
      isEmpty () {
        return this.slots && Object.keys(this.slots.rows).length === 0;
      },
      // Context metrics derived from the grid the endpoint already returns:
      // total agent-slots per type, days with availability, and the busiest slot.
      summary () {
        if (!this.slots) {
          return null;
        }

        let totals = {};
        let peak = 0;
        let peakTime = null;

        let rows = this.slots.rows;
        for (let time in rows) {
          let byDay = rows[time];
          for (let day in byDay) {
            let types = byDay[day];
            for (let type in types) {
              let count = types[type];
              totals[type] = (totals[type] || 0) + count;
              if (count > peak) {
                peak = count;
                peakTime = time;
              }
            }
          }
        }

        let ordered = [];
        for (let key in APPOINTMENT_TYPE_LABELS) {
          if (totals[key]) {
            ordered.push({type: key, label: APPOINTMENT_TYPE_LABELS[key], total: totals[key]});
          }
        }

        return {types: ordered, days: this.days.length, peak: peak, peakTime: peakTime};
      }
    },
    methods: {
      labelClass (type) {
        return APPOINTMENT_TYPE_COLORS[type] || 'label-default';
      },
      iconClass (type) {
        return APPOINTMENT_TYPE_ICONS[type] || 'fa-question';
      },
      cellTypes (hour, day) {
        let byDay = this.slots.rows[hour];
        return (byDay && byDay[day]) || {};
      },
      applyFilters: _.debounce(async function (params) {
        this.params = params;
        await this.getData();
      }, 500),
      async getSlots () {
        let response = await API.agents.slots(this.params);
        this.slots = response.body;
      },
      async getData (withFilters) {
        this.loaded = false;

        try {
          await this.getSlots();
        } catch (err) {
          Vue.toast("Unexpected error. Cannot retrieve data from server.", {className: ['et-error']});
          console.log(err);
        }

        if (withFilters) {
          let optionsResponse = await API.agents.options();
          let filters = optionsResponse.body.actions.GET;
          // Only active agents can have slots, so the status filter is irrelevant here.
          delete filters.statuses;
          this.filters = filters;
        }

        this.loaded = true;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .slots {
    .dashboard-header {
      padding: .5rem;
    }

    .summary {
      display: flex;
      flex-wrap: wrap;
      padding: 0 .5rem .75rem;

      .summary-card {
        display: inline-flex;
        align-items: center;
        margin: 0 .5rem .35rem 0;
        padding: .25rem .6rem;
        background: #f2f6f9;
        border-radius: 3px;
        font-size: .95rem;

        .label {
          margin-right: .35rem;
        }

        strong {
          margin-right: .25rem;
          font-size: 1.1rem;
        }

        .muted {
          color: #888;
        }
      }
    }

    // White cells separated by a darker grid so each day/time column reads clearly.
    .slots-table.table {
      border-collapse: separate;
      border-spacing: 3px;
      background-color: #cdd9e2;

      > thead > tr > th,
      > tbody > tr > th,
      > tbody > tr > td {
        background-color: #fff;
        border: none;
        white-space: nowrap;
        vertical-align: middle;
      }

      // Tint the header row and the time (row-header) column to set them apart.
      > thead > tr > th,
      > tbody > tr > th {
        background-color: #eaf0f5;
        font-weight: 600;
      }

      td .label {
        margin-right: .15rem;
      }
    }

    .no-data {
      padding: 1rem;
      color: #888;
    }
  }
</style>



// WEBPACK FOOTER //
// AgentSlots.vue?37079aa8